import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Compass, SearchX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import AgreementsShell from "@/components/agreements/AgreementsShell";
import FilterBar from "@/components/agreements/FilterBar";
import DocumentCard from "@/components/agreements/DocumentCard";
import CategoryIcon from "@/components/agreements/CategoryIcon";
import LegalDisclaimer from "@/components/agreements/LegalDisclaimer";
import { CATEGORIES, CATEGORY_BY_ID, FILTERS, ACCENT_CLASSES } from "@/data/agreements/categories";
import { STAGE_IDS, IMPORTANCE_IDS } from "@/data/agreements/stages";
import { DOCUMENTS, searchDocuments, docsForFilter } from "@/data/agreements/documentLibrary";
import { eventsApi } from "@/lib/agreements/api";

/**
 * DocumentLibrary — route: /agreements/library
 *
 * Free-text search, multi-select founder filters, a stage filter and an importance filter.
 * The active view is reflected in the query string (?q=&filters=&stage=&importance=) so a
 * filtered library can be bookmarked or sent to a co-founder, and is read back on mount.
 *
 * No search  → grouped by category, with a count per group.
 * Searching  → one flat list, in the ranking searchDocuments() returns.
 */
export default function DocumentLibrary() {
  const [params, setParams] = useSearchParams();
  const { hash } = useLocation();

  // The URL seeds the initial state; after that the state drives the URL.
  const [query, setQuery] = useState(() => params.get("q") || "");
  const [filters, setFilters] = useState(() => parseFilters(params.get("filters")));
  const [stage, setStage] = useState(() => oneOf(params.get("stage"), STAGE_IDS));
  const [importance, setImportance] = useState(() => oneOf(params.get("importance"), IMPORTANCE_IDS));

  // Keep the query string in step with the controls. `replace` so filtering does not
  // fill the back button with every keystroke.
  useEffect(() => {
    const next = new URLSearchParams();
    const q = query.trim();
    if (q) next.set("q", q);
    if (filters.length) next.set("filters", filters.join(","));
    if (stage) next.set("stage", stage);
    if (importance) next.set("importance", importance);
    setParams(next, { replace: true });
  }, [query, filters, stage, importance, setParams]);

  const searching = query.trim().length > 0;

  // Multi-select filters read as OR: show me anything touching any of these.
  const allowedIds = useMemo(() => {
    if (!filters.length) return null;
    const ids = new Set();
    for (const label of filters) for (const doc of docsForFilter(label)) ids.add(doc.id);
    return ids;
  }, [filters]);

  const results = useMemo(() => {
    const base = searching ? searchDocuments(query, { limit: DOCUMENTS.length }) : DOCUMENTS;
    return base.filter(
      (doc) =>
        (!allowedIds || allowedIds.has(doc.id)) &&
        (!stage || (doc.stages || []).includes(stage)) &&
        (!importance || doc.importance === importance),
    );
  }, [searching, query, allowedIds, stage, importance]);

  // Grouped view. Categories keep their canonical order; anything with an unrecognised
  // category id still gets shown rather than silently disappearing.
  const groups = useMemo(() => {
    if (searching) return [];
    const byCategory = new Map();
    for (const doc of results) {
      if (!byCategory.has(doc.category)) byCategory.set(doc.category, []);
      byCategory.get(doc.category).push(doc);
    }
    const ordered = CATEGORIES.filter((c) => byCategory.has(c.id)).map((c) => ({
      id: c.id,
      name: c.name,
      blurb: c.blurb,
      docs: byCategory.get(c.id),
    }));
    for (const [id, docs] of byCategory) {
      if (!CATEGORY_BY_ID[id]) ordered.push({ id, name: id || "Uncategorised", blurb: "", docs });
    }
    return ordered;
  }, [results, searching]);

  // One view_category event per distinct filter combination the founder lands on.
  const lastTracked = useRef(null);
  useEffect(() => {
    const signature = `${filters.join("|")}/${stage}/${importance}`;
    if (lastTracked.current === signature) return;
    lastTracked.current = signature;
    if (!filters.length && !stage && !importance) return;
    const matching = filters.length === 1
      ? CATEGORIES.filter((c) => (c.filters || []).includes(filters[0]))
      : [];
    eventsApi.track({
      type: "view_category",
      categoryId: matching.length === 1 ? matching[0].id : undefined,
      context: `library:${signature}`.slice(0, 120),
    });
  }, [filters, stage, importance]);

  // Support /agreements/library#cat-<categoryId> deep links from the dashboard tiles.
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, groups.length]);

  const clearAll = useCallback(() => {
    setQuery("");
    setFilters([]);
    setStage("");
    setImportance("");
  }, []);

  const hasAnyFilter = searching || filters.length > 0 || Boolean(stage) || Boolean(importance);

  return (
    <AgreementsShell
      wide
      crumbs={[{ label: "Library" }]}
      title="Document library"
      subtitle={`All ${DOCUMENTS.length} agreements a CPG brand runs into, across ${CATEGORIES.length} categories. Search by name, or filter down to the part of the business you are working on.`}
      actions={
        <Button asChild variant="outline">
          <Link to="/agreements/finder">
            <Compass className="mr-2 h-4 w-4" /> Not sure what you need?
          </Link>
        </Button>
      }
    >
      <div className="mb-6">
        <FilterBar
          query={query}
          onQuery={setQuery}
          filters={filters}
          onFilters={setFilters}
          stage={stage}
          onStage={setStage}
          importance={importance}
          onImportance={setImportance}
          resultCount={results.length}
          onClear={clearAll}
        />
      </div>

      {results.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center sm:p-12">
          <SearchX className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <h2 className="text-base font-semibold">No documents match</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            {searching
              ? `Nothing in the library matches "${query.trim()}" with these filters. Try a shorter word — "co-packer", "distributor", "NDA" — or clear the filters.`
              : "That combination of filters has no documents in it. Clear them and start again."}
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button onClick={clearAll}>Clear filters</Button>
            <Button asChild variant="outline">
              <Link to="/agreements/finder">
                <Compass className="mr-2 h-4 w-4" /> Use the finder instead
              </Link>
            </Button>
          </div>
        </div>
      )}

      {/* Searching → one ranked, flat list. */}
      {results.length > 0 && searching && (
        <section>
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {results.length} {results.length === 1 ? "result" : "results"} for "{query.trim()}"
          </h2>
          <div className="card-grid-auto gap-3">
            {results.map((doc) => (
              <DocumentCard key={doc.id} doc={doc} />
            ))}
          </div>
        </section>
      )}

      {/* Browsing → grouped by category with a count per group. */}
      {results.length > 0 &&
        !searching &&
        groups.map((group) => {
          const accent = ACCENT_CLASSES[CATEGORY_BY_ID[group.id]?.accent] || null;
          return (
            <section key={group.id} id={`cat-${group.id}`} className="mb-10 scroll-mt-24">
              <div
                className={cn(
                  "mb-4 flex items-start gap-3 rounded-xl border-l-4 py-2 pl-3",
                  accent?.border || "border-slate-500",
                )}
              >
                <CategoryIcon categoryId={group.id} size="lg" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest ring-1",
                        accent?.bg,
                        accent?.text,
                        accent?.ring,
                      )}
                    >
                      <span className={cn("h-1.5 w-1.5 rounded-full", accent?.dot)} />
                      {CATEGORY_BY_ID[group.id]?.short || group.name}
                    </span>
                    <span className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">
                      {group.docs.length} {group.docs.length === 1 ? "document" : "documents"}
                    </span>
                  </div>
                  <h2 className="mt-1 text-xl font-bold tracking-tight text-neutral-900">
                    {group.name}
                  </h2>
                  {group.blurb && <p className="mt-0.5 text-sm text-neutral-500">{group.blurb}</p>}
                </div>
              </div>
              <div className="card-grid-auto gap-3">
                {group.docs.map((doc) => (
                  <DocumentCard key={doc.id} doc={doc} />
                ))}
              </div>
            </section>
          );
        })}

      {hasAnyFilter && results.length > 0 && (
        <p className="mb-8 text-center text-sm text-muted-foreground">
          Showing {results.length} of {DOCUMENTS.length}.{" "}
          <button type="button" onClick={clearAll} className="font-medium underline">
            Clear filters
          </button>
        </p>
      )}

      <LegalDisclaimer variant="library" />
    </AgreementsShell>
  );
}

/** Only accept filter labels the library actually knows about. */
function parseFilters(raw) {
  if (!raw) return [];
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => FILTERS.includes(s));
}

/** Only accept a value that exists in the given id list. */
function oneOf(raw, allowed) {
  const value = (raw || "").trim();
  return allowed.includes(value) ? value : "";
}
